// =============================================================================
// DownloadReportModal — filtered Excel report download
// =============================================================================
// Shared by the Orders and Delivery report buttons: pick a period, shop,
// salesperson, customer, and payment status, then download an Excel workbook
// of exactly that slice. Salespeople options come from /orders/filters/options
// (scoped to whichever shops the current user can already see).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import { downloadBlob, XLSX_MIME } from '../lib/download';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';

interface Shop {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface FilterOptions {
  salespeople: Array<{ id: string; name: string }>;
}

const PAYMENT_STATUSES = [
  { value: '', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'partial', label: 'Partial' },
  { value: 'unpaid', label: 'Unpaid' },
];

export function DownloadReportModal({
  title,
  endpoint,
  extraParams,
  filenamePrefix,
  showShop,
  defaultFrom,
  defaultTo,
  onClose,
}: {
  title: string;
  endpoint: string;
  /** Fixed params always sent alongside the chosen filters, e.g. { format: 'excel' }. */
  extraParams?: Record<string, string>;
  filenamePrefix: string;
  showShop: boolean;
  defaultFrom?: string;
  defaultTo?: string;
  onClose: () => void;
}): JSX.Element {
  const [from, setFrom] = useState(defaultFrom ?? '');
  const [to, setTo] = useState(defaultTo ?? '');
  const [shopId, setShopId] = useState('');
  const [salespersonId, setSalespersonId] = useState('');
  const [q, setQ] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const shops = useQuery({
    queryKey: ['shops'],
    queryFn: () => apiGet<Shop[]>('/shops'),
    enabled: showShop,
  });
  const options = useQuery({
    queryKey: ['orders-filter-options'],
    queryFn: () => apiGet<FilterOptions>('/orders/filters/options'),
  });

  const download = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await downloadBlob(
        endpoint,
        {
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(shopId ? { shopId } : {}),
          ...(salespersonId ? { salespersonId } : {}),
          ...(q ? { q } : {}),
          ...(paymentStatus ? { paymentStatus } : {}),
          ...extraParams,
        },
        XLSX_MIME,
        `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
      onClose();
    } catch {
      setErr('Could not download the report — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      subtitle="Choose the period and filters for this report."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void download()} disabled={busy}>
            {busy ? 'Downloading…' : 'Download Excel'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>

        {showShop && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Shop</span>
            <select value={shopId} onChange={(e) => setShopId(e.target.value)} className="kc-input">
              <option value="">All shops</option>
              {(shops.data ?? [])
                .filter((s) => s.isActive)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} {s.name}
                  </option>
                ))}
            </select>
          </label>
        )}

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Salesperson</span>
          <select value={salespersonId} onChange={(e) => setSalespersonId(e.target.value)} className="kc-input">
            <option value="">All salespeople</option>
            {(options.data?.salespeople ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Payment</span>
          <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className="kc-input">
            {PAYMENT_STATUSES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </label>

        <Input
          label="Customer"
          placeholder="Name, company, or order no…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        {err && <div className="text-sm text-negative">{err}</div>}
      </div>
    </Modal>
  );
}
